import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, IconButton, Skeleton, Typography } from '@mui/material';
import { MdArrowBack, MdEdit, MdInventory2, MdPlayCircle } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { FONT, StatusChip, authToken, fmtDate, img, inr, isVideoUrl, safeHtml, stockOf, yellowBtn } from '../../components/Products/productShared';

const Card = ({ title, accent, children, right }) => (
    <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
            {right}
        </Box>
        {children}
    </Box>
);
const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.95, borderBottom: '1px dashed #e2e8f0' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
        <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
    </Box>
);
const Rich = ({ html }) => (
    <Box sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.65, color: '#1e293b', '& ul,& ol': { pl: 3 }, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: safeHtml(html) || '<span style="color:#94a3b8">Nothing added.</span>' }} />
);

const discountOf = (mrp, sp) => (Number(mrp) > 0 && Number(sp) > 0 && Number(sp) < Number(mrp) ? Math.round(((mrp - sp) / mrp) * 100) : 0);

const ViewProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [shown, setShown] = useState(0);

    useEffect(() => {
        setLoading(true);
        getData(PRODUCT_ROUTES.GET_PRODUCT_DETAILS + productId, null, authToken(), false)
            .then((res) => {
                const prod = res?.product || res?.data || res;
                if (!prod?._id) throw new Error('missing');
                setP(prod);
            })
            .catch(() => setError('This product could not be found. It may have been deleted.'))
            .finally(() => setLoading(false));
    }, [productId]);

    if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={110} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} /></Box>;
    if (error || !p) {
        return (
            <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button onClick={() => navigate('/productmanagement')} sx={yellowBtn}>Back to products</Button>
            </Box>
        );
    }

    const media = [...(p.product_images || []), ...(p.productVideos || [])];
    const current = media[shown] || media[0];
    const stock = stockOf(p);
    const dims = p.dimensions || {};
    const dimText = dims.length || dims.width || dims.height ? `${dims.length || 0} × ${dims.width || 0} × ${dims.height || 0} ${dims.unit || ''}` : null;
    const vis = (p.visibility || []).map((v) => v.toUpperCase()).join(' · ');
    const owner = p.ownership === 'Seller Product' ? (p.seller_id?.business_name || p.seller_id?.name || 'Seller') : 'Own product';
    const by = p.createdBy ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''}`.trim() || p.createdBy.email : null;

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1200, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
                <IconButton onClick={() => navigate('/productmanagement')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>{p.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <StatusChip status={p.status} />
                        <Chip size="small" label={p.isPublished ? 'Live' : 'Not live'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: p.isPublished ? '#dcfce7' : '#f1f5f9', color: p.isPublished ? '#166534' : '#475569' }} />
                        <Chip size="small" label={stock.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: stock.bg, color: stock.fg }} />
                        {p.badge && <Chip size="small" label={p.badge} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: '#ede9fe', color: '#5b21b6' }} />}
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{p.productCode || ''}{p.gsin ? ` · GSIN ${p.gsin}` : ''}</Typography>
                    </Box>
                </Box>
                <Button startIcon={<MdEdit />} variant="contained" onClick={() => navigate(`/productmanagement/edit/${productId}`)} sx={yellowBtn}>Edit product</Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', p: 2 }}>
                        <Box sx={{ aspectRatio: '1 / 1', borderRadius: '12px', overflow: 'hidden', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!current ? <MdInventory2 size={56} color="#cbd5e1" /> : isVideoUrl(current) ? <video src={img(current)} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <img src={img(current)} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
                        </Box>
                        {media.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                {media.map((m, i) => (
                                    <Box key={m + i} onClick={() => setShown(i)} sx={{ position: 'relative', cursor: 'pointer', width: 58, height: 58, borderRadius: '10px', overflow: 'hidden', border: '2px solid', borderColor: i === shown ? '#f59e0b' : '#e2e8f0' }}>
                                        {isVideoUrl(m) ? <Box sx={{ width: '100%', height: '100%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MdPlayCircle size={26} color="#fff" /></Box> : <Avatar variant="square" src={img(m)} sx={{ width: '100%', height: '100%' }} />}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Card title="Pricing" accent="#22c55e">
                        {p.isFree ? (
                            <>
                                <Row label="Type" value="Free gift product" />
                                <Row label="Unlocked at cart amount" value={inr(p.thresholdAmount)} />
                                <Row label="Added automatically" value={p.autoAdd ? 'Yes' : 'No'} />
                            </>
                        ) : (
                            <>
                                <Row label="MRP" value={inr(p.b2c_mrp)} />
                                <Row label="B2C price" value={<>{inr(p.b2c_sellingPrice)}{discountOf(p.b2c_mrp, p.b2c_sellingPrice) ? <Chip size="small" label={`${discountOf(p.b2c_mrp, p.b2c_sellingPrice)}% off`} sx={{ ml: 1, height: 20, fontWeight: 700, background: '#fef3c7', color: '#92400e' }} /> : null}</>} />
                                <Row label="B2B price" value={<>{inr(p.b2b_sellingPrice)}{discountOf(p.b2c_mrp, p.b2b_sellingPrice) ? <Chip size="small" label={`${discountOf(p.b2c_mrp, p.b2b_sellingPrice)}% off`} sx={{ ml: 1, height: 20, fontWeight: 700, background: '#fef3c7', color: '#92400e' }} /> : null}</>} />
                            </>
                        )}
                        <Row label="GST" value={p.gst ? `${p.gst}%` : null} />
                        <Row label="HSN code" value={p.hsn_code} />
                    </Card>
                    <Card title="Stock" accent="#f59e0b">
                        <Row label="Available now" value={<Chip size="small" label={stock.label} sx={{ fontWeight: 700, background: stock.bg, color: stock.fg }} />} />
                        <Row label="Low-stock alert at" value={p.minimumStockQuantity ?? 0} />
                        <Row label="Pack size" value={p.unitValue ? `${p.unitValue} ${p.unitType === 'weight' ? p.unitName || '' : 'unit(s)'}`.trim() : null} />
                        <Row label="Container" value={p.container_type ? p.container_type.replace(/_/g, ' ') : null} />
                        <Row label="Size" value={dimText} />
                        <Row label="Weight" value={dims.weight ? `${dims.weight} ${dims.weightUnit || ''}` : null} />
                    </Card>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Card title="Overview" accent="#6366f1">
                        <Row label="Category" value={[p.category_id?.title, p.subcategory_id?.title].filter(Boolean).join(' › ')} />
                        <Row label="Brand" value={p.brand} />
                        <Row label="UPC" value={p.upc} />
                        <Row label="Ideal for" value={p.ideal_for ? <span style={{ textTransform: 'capitalize' }}>{p.ideal_for}</span> : null} />
                        <Row label="Applied for" value={p.applied_for} />
                        <Row label="Colour" value={p.color} />
                        <Row label="Country of origin" value={p.country_of_orgin} />
                        <Row label="Sold to" value={vis} />
                        <Row label="Cash on delivery" value={p.codAvailable ? 'Available' : 'Not available'} />
                        <Row label="Returns" value={p.return_policy ? `${p.return_policy} days` : 'No returns'} />
                        <Row label="Owner" value={owner} />
                        <Row label="Supplier" value={p.supplier_id?.name} />
                        <Row label="Added by" value={by} />
                        <Row label="Added / updated" value={`${fmtDate(p.createdAt)} · ${fmtDate(p.updatedAt)}`} />
                    </Card>
                    <Card title="Description" accent="#0ea5e9"><Typography sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.65, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{p.description || 'Nothing added.'}</Typography></Card>
                    <Card title="Key features" accent="#ec4899"><Rich html={p.key_features} /></Card>
                    <Card title="How to use" accent="#8b5cf6"><Rich html={p.how_to_use} /></Card>
                    {(p.ingredients || p.skin_type || p.hair_type || p.fragrance_notes || p.is_vegan || p.cruelty_free || p.organic) && (
                        <Card title="Beauty details" accent="#14b8a6">
                            <Row label="Ingredients" value={p.ingredients} />
                            <Row label="Skin type" value={p.skin_type} />
                            <Row label="Hair type" value={p.hair_type} />
                            <Row label="Fragrance notes" value={p.fragrance_notes} />
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                {p.is_vegan && <Chip size="small" label="Vegan" sx={{ fontWeight: 700 }} />}
                                {p.cruelty_free && <Chip size="small" label="Cruelty free" sx={{ fontWeight: 700 }} />}
                                {p.organic && <Chip size="small" label="Organic" sx={{ fontWeight: 700 }} />}
                            </Box>
                        </Card>
                    )}
                    {(p.tags?.length > 0) && (
                        <Card title="Keywords" accent="#94a3b8">
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>{p.tags.map((t) => <Chip key={t} size="small" label={t} />)}</Box>
                        </Card>
                    )}
                    {(p.manufacturerName || p.manufacturerAddress || p.manufacturingLicenseNumber || p.meta_title || p.slug) && (
                        <Card title="Manufacturer & SEO" accent="#64748b">
                            <Row label="Manufacturer" value={p.manufacturerName} />
                            <Row label="Address" value={p.manufacturerAddress} />
                            <Row label="Licence number" value={p.manufacturingLicenseNumber} />
                            <Row label="Web address" value={p.slug} />
                            <Row label="Meta title" value={p.meta_title} />
                            <Row label="Meta description" value={p.meta_description} />
                        </Card>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ViewProduct;
